import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, Skeleton, Empty } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import CustomPagination from '../../components/common/CustomPagination';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import httpClient from '../../utils/HttpClient';
import { FASHION_URL } from '../../constants/Url';
import NotFoundPage from '../NotFoundPage';
import AuthModal from '../../components/auth/AuthModal';
import FashionModals from '../../components/FashionModals';
import PROModal from '../../components/PROModal';
import { useMemberAccess } from '../../hooks/useMemberAccess';

export default function FashionDetailPage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const authModalRef = useRef();
    const appDownloadRef = useRef();
    const user = useSelector((state) => state.authReducer);
    let token = user && user?.token ? user?.token : null;
    const { hasNoActiveMembership } = useMemberAccess();
    const [isMemberGateOpen, setIsMemberGateOpen] = useState(false);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageLoading, setPageLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [activeModalData, setActiveModalData] = useState(null);

    // Pagination states for all data list view
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(9);

    const fetchDetail = async () => {
        try {
            setLoading(true);
            const res = await httpClient.get(`${FASHION_URL}/${id}`)
                .then(res => res.data)
                .catch(error => ({ status: 500, message: error?.message }));

            if (res && res.status === 200 && res.data) {
                setData(res.data);
                setNotFound(false);
            } else {
                setNotFound(true);
            }
        } catch (error) {
            console.error('fetchDetail -> error', error);
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllFashions = async () => {
        try {
            setLoading(true);
            const res = await httpClient.get(`${FASHION_URL}?pageNo=1&pageSize=100&releaseYear=ALL`)
                .then(res => res.data)
                .catch(error => ({ status: 500, message: error?.message }));

            if (res && res.status === 200 && res.data) {
                setData(res.data);
                setNotFound(false);
            } else {
                setNotFound(true);
            }
        } catch (error) {
            console.error('fetchAllFashions -> error', error);
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    };

    const isListMode = !id || id === 'detail';

    useEffect(() => {
        if (isListMode && data.length > 0) {
            setPageLoading(true);
            const timer = setTimeout(() => {
                setPageLoading(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [currentPage, pageSize, isListMode, data.length]);

    useEffect(() => {
        if (id && id !== 'detail') {
            fetchDetail();
        } else {
            fetchAllFashions();
        }
    }, [id]);

    useEffect(() => {
        if (id && id !== 'detail' && user?.token && hasNoActiveMembership()) {
            setIsMemberGateOpen(true);
        }
    }, [id, user?.token, hasNoActiveMembership]);

    const handleShowDownloadApp = (item) => {
        if (!item) return;
        setActiveModalData(item);
        if (!token) {
            authModalRef.current.open();
            return;
        }
        if (!user?.user?.completedTS) {
            authModalRef.current.open('REGISTER');
            return;
        }
        // Gate: expired trial or no membership cannot read fashion content
        if (hasNoActiveMembership()) {
            setIsMemberGateOpen(true);
            return;
        }
        appDownloadRef.current?.showModal(item);
    };



    if (notFound || !data) {
        return <NotFoundPage />;
    }

    // Pagination slice
    const paginatedData = isListMode && Array.isArray(data)
        ? data.slice((currentPage - 1) * pageSize, currentPage * pageSize)
        : [];

    return (
        <div className="w-full min-h-screen pb-16 bg-[#f7f9fc]">
            <AuthModal ref={authModalRef} />
            {isListMode ? (
                /* Show All Data Mode - Matching The Design Mockup */
                <div className="w-full">
                    {/* Header Banner Section */}
                    <div className="w-full py-10 px-4 text-center bg-gradient-to-r from-[#4d9dec] via-[#637cfb] to-[#a380f9] text-white">
                        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide m-0 text-white mb-2">
                            {t('menu_theme')}
                        </h1>
                    </div>
                    <div className="max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 mt-10">
                        {(loading || pageLoading) ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <div key={index} className="bg-white rounded-lg overflow-hidden border border-gray-100 shadow-sm p-5 sm:p-6 flex flex-col gap-4">
                                        <div className="w-full aspect-[4/3] rounded-md overflow-hidden relative bg-gray-50 flex items-center justify-center">
                                            <Skeleton.Image className="!w-full !h-full" active={true} style={{ width: '100%', height: '100%' }} />
                                        </div>
                                        <Skeleton.Input active size="default" style={{ width: '70%', height: '22px' }} />
                                        <Skeleton active paragraph={{ rows: 2 }} title={false} className="mt-2" />
                                        <div className="flex justify-between items-center pt-4 border-t border-gray-50 mt-auto">
                                            <Skeleton.Input active size="small" style={{ width: '30%', height: '16px' }} />
                                            <Skeleton.Input active size="small" style={{ width: '25%', height: '16px' }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : Array.isArray(data) && data.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
                                {paginatedData.map((item) => (
                                    <div
                                        key={item._id}
                                        className="bg-white rounded-lg overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full"
                                    >
                                        {/* Image cover */}
                                        {item.picUrl && (
                                            <div className="w-full aspect-[4/3] overflow-hidden relative">
                                                <img
                                                    src={item.picUrl}
                                                    alt={item.title}
                                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 cursor-pointer"
                                                    onClick={() => handleShowDownloadApp(item)}
                                                />
                                            </div>
                                        )}

                                        {/* Content block */}
                                        <div className="p-5 sm:p-6 flex flex-col flex-1">
                                            <h3
                                                className="text-[16px] sm:text-[18px] font-bold text-gray-900 leading-snug cursor-pointer hover:text-[#637cfb] transition-colors mb-3 line-clamp-1"
                                                onClick={() => handleShowDownloadApp(item)}
                                            >
                                                {item.title}
                                            </h3>
                                            <p className="text-gray-500 text-[13px] leading-relaxed line-clamp-3 mb-6 flex-1">
                                                {item.description}
                                            </p>

                                            {/* Footer details */}
                                            <div className="flex justify-between items-center pt-4 border-t border-gray-50 mt-auto">
                                                <span className="text-[12px] text-gray-400">
                                                    {item.releaseYear ? `${item.releaseYear}` : ''} {t('text_series')}
                                                </span>
                                                {item.fashionId && (
                                                    <span
                                                        onClick={() => handleShowDownloadApp(item)}
                                                        className="text-[12px] gradient-text-button font-bold flex items-center gap-1 cursor-pointer transition-colors"
                                                    >
                                                        {t('text_in_depth')} &rarr;
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <Empty description={t('text_no_work')} />
                        )}

                        {/* Pagination component */}
                        {(loading || pageLoading || (Array.isArray(data) && data.length > 0)) && (
                            <div className="flex justify-end mt-12">
                                <CustomPagination
                                    current={currentPage}
                                    pageSize={pageSize}
                                    total={(loading || pageLoading) ? 9 : (data.length || 0)}
                                    pageSizeOptions={[9, 18, 27, 36, 45]}
                                    onChange={(page, limit) => {
                                        if (limit !== pageSize) {
                                            setPageSize(limit);
                                            setCurrentPage(1);
                                        } else {
                                            setCurrentPage(page);
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            ) : loading ? (
                /* Single Detail Mode Skeleton */
                <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 mt-6 flex flex-col gap-6">
                    <div 
                        onClick={() => {
                            if (window.history.state && window.history.state.idx > 0) {
                                navigate(-1);
                            } else {
                                navigate('/fashion/detail');
                            }
                        }} 
                        className="inline-flex items-center gap-2 cursor-pointer text-gray-500 hover:text-black transition-colors font-medium text-sm sm:text-base self-start"
                    >
                        <ArrowLeftOutlined className="text-base" />
                        <span>{t('text_back_previous_page')}</span>
                    </div>

                    <section className="w-full">
                        <div className="flex justify-between items-center mb-4 sm:mb-6 pb-2 border-b border-gray-100">
                            <h2 className="text-[17px] sm:text-[20px] font-bold text-gray-900 tracking-wide m-0">
                                {t('menu_theme')}
                            </h2>
                            <span className="text-[12px] sm:text-[13px] text-gray-400 font-medium whitespace-nowrap">
                                {t('button_view_more')}
                            </span>
                        </div>
                        <div className="flex flex-col md:flex-row overflow-hidden bg-white rounded-lg shadow-sm border border-gray-50 p-4 gap-4">
                            <div className="w-full md:w-[45%] h-[250px] sm:h-[380px] md:h-[480px]">
                                <div className="w-full h-full bg-gray-100 animate-pulse rounded flex items-center justify-center">
                                    <svg className="w-12 h-12 text-gray-300" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor" viewBox="0 0 640 512">
                                        <path d="M480 80C480 35.82 515.8 0 560 0C604.2 0 640 35.82 640 80C640 124.2 604.2 160 560 160C515.8 160 480 124.2 480 80zM0 456.1C0 445.6 2.964 435.3 8.551 426.4L225.3 81.01C231.9 70.42 243.5 64 256 64C268.5 64 280.1 70.42 286.8 81.01L412.7 281.7L460.9 202.7C464.1 196.1 472.2 192 480 192C487.8 192 495 196.1 499.1 202.7L631.1 419.1C636.9 428.6 640 439.7 640 450.9C640 484.6 612.6 512 578.9 512H55.91C25.03 512 .0006 486.1 .0006 456.1L0 456.1z" />
                                    </svg>
                                </div>
                            </div>
                            <div className="w-full md:w-[55%] p-6 sm:p-10 md:p-16 flex flex-col justify-center">
                                <Skeleton.Input active block size="large" />
                                <Skeleton active paragraph={{ rows: 4 }} className="mt-4" />
                                <Skeleton.Button active size="large" className="mt-8 w-[260px]" style={{ width: '260px', height: '48px' }} />
                            </div>
                        </div>
                    </section>
                </div>
            ) : (
                /* Single Detail Mode */
                <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 mt-6 flex flex-col gap-6">
                    <div 
                        onClick={() => {
                            if (window.history.state && window.history.state.idx > 0) {
                                navigate(-1);
                            } else {
                                navigate('/fashion/detail');
                            }
                        }} 
                        className="inline-flex items-center gap-2 cursor-pointer text-gray-500 hover:text-black transition-colors font-medium text-sm sm:text-base self-start"
                    >
                        <ArrowLeftOutlined className="text-base" />
                        <span>{t('text_back_previous_page')}</span>
                    </div>

                    <section className="w-full">
                        <div className="flex justify-between items-center mb-4 sm:mb-6 pb-2 border-b border-gray-100">
                            <h2 className="text-[17px] sm:text-[20px] font-bold text-gray-900 tracking-wide m-0">
                                {t('menu_theme')}
                            </h2>
                            <span
                                onClick={() => navigate('/fashion/detail')}
                                className="text-[12px] sm:text-[13px] text-gray-400 hover:text-black cursor-pointer font-medium transition-colors whitespace-nowrap"
                            >
                                {t('button_view_more')}
                            </span>
                        </div>

                        <div className="flex flex-col md:flex-row overflow-hidden bg-white rounded-lg shadow-sm border border-gray-50">
                            {/* Image */}
                            <div className="w-full md:w-[45%] h-[250px] sm:h-[380px] md:h-[480px] overflow-hidden relative flex-shrink-0">
                                <img
                                    src={data.picUrl}
                                    alt={data.title}
                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 cursor-pointer"
                                    onClick={() => handleShowDownloadApp(data)}
                                />
                            </div>

                            {/* Details */}
                            <div className="w-full md:w-[55%] p-6 sm:p-10 md:p-16 flex flex-col justify-center items-start gap-4">
                                <span className="text-[14px] sm:text-[16px] font-semibold tracking-wider uppercase bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent">
                                    {(data.releaseYear ?? "")} {t('text_series')}
                                </span>
                                <h3 className="text-[28px] md:text-[34px] font-bold text-gray-950 leading-tight mb-2">
                                    {data.title}
                                </h3>

                                <p className="text-gray-500 text-[14px] sm:text-[15px] leading-relaxed line-clamp-4 m-0 whitespace-pre-wrap">
                                    {data.description}
                                </p>

                                {data.fashionId && (
                                    <div className="w-full sm:w-auto mt-8 p-[2px] bg-gradient-to-r from-[#50c7f8] via-[#7887fd] to-[#df97fb] rounded-md shadow-md">
                                        <button
                                            onClick={() => handleShowDownloadApp(data)}
                                            className="w-full sm:w-[260px] h-12 flex items-center justify-center bg-gradient-to-r from-[#21163e] to-[#252554] text-white hover:opacity-90 transition-all rounded-md"
                                        >
                                            <span className="text-[13px] sm:text-[14px] tracking-widest text-center font-medium">
                                                {t('text_full_series')}
                                            </span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                </div>
            )}

            <FashionModals ref={appDownloadRef} />
            <AuthModal ref={authModalRef} />
            <PROModal open={isMemberGateOpen} onCancel={() => setIsMemberGateOpen(false)} />
        </div>
    );
}